import LoggerService,{formatTrace,formatInfo} from '../../LoggerService';
import {
  _checkTokens,
  _checkErrors,
  _tryOrRefresh,
  I_COMMON_GET_ALL_PARAMS,
  I_COMMON_GET_ITEM_PARAMS,
  I_COMMON_DELETE_PARAMS,
  I_COMMON_IMPORT_PARAMS,
  I_COMMON_CREATE_PARAMS,
  I_COMMON_PUT_PARAMS,
} from './_base';

import I_CUSTOMERS,{I_CUSTOMER,I_IMPORT_CUSTOMERS_OK,I_CUSTOMER_REFERENTIAL_SITE} from '../interfaces/I_CUSTOMERS';
import {
  getCustomersResponse,
  postCustomerResponse,
  deleteCustomerResponse,
  getCustomerResponse,
  putCustomerResponse,
  importCustomersResponse,
} from '../ApiEndpoints';

// ---------------------------------
// --- ("AUDITED") CUSTOMERS
// ---------------------------------

const logger = LoggerService.getInstance();

interface I_GET_PARAMS extends I_COMMON_GET_ALL_PARAMS {
  type?: string;
  name?: string;
  id_customer?: string;
  referentiel_name?: string;
  referentiel_number?: string;
  mode?: string;  // 'light'
}
interface I_CUSTOMER_FIELDS {
  /** type : client / organisme */
  type: string;
  /** nom du client */
  name: string;
  /** matricule de l'organisme (undefined si client) */
  id_customer?: number;
  /** liste des agréments de l'organisme (undefined si client) */
  referentials?: I_CUSTOMER_REFERENTIAL_SITE[];
}
interface I_CREATE_CUSTOMER_PARAMS extends I_COMMON_CREATE_PARAMS, I_CUSTOMER_FIELDS {}
interface I_PUT_CUSTOMER_PARAMS extends I_COMMON_PUT_PARAMS, I_CUSTOMER_FIELDS {}


// ----

const isMissingField = (item:I_CUSTOMER_FIELDS) => {
  const typeKO = item.type === undefined;
  const nameKO = !item.name || item.name === null || item.name.trim() === '';
  const idcustomerKO = item.type === 'organisme' && item.id_customer === undefined;
  const referentialsKO = false;
  // TODO : check pour chaque referential, qu'on a le nom et l'id
  return typeKO || nameKO || idcustomerKO || referentialsKO;
}

// ----



/**
 * Récupère la liste des organismes audités
 * @param obj
 * @returns
 */
const getList = async ({accessToken,refreshToken,page,dispatch,state,type,name,id_customer,referentiel_name,referentiel_number,mode}:I_GET_PARAMS):Promise<I_CUSTOMERS> => {
  logger.debug('ApiServices - getCustomers - ...');
  // pre-verification qu'on a bien des token, sinon sert a rien de faire la requete
  await _checkTokens(accessToken,refreshToken);
  // requete le endpoint
  const response = await _tryOrRefresh({
    request: (usedAccessToken:string) => {
      return getCustomersResponse({accessToken:usedAccessToken,page,type,name,id_customer,referentiel_name,referentiel_number,mode});
    },
    accessToken: accessToken || '', // normalement c'est non vide car on a fait un checkTokens
    refreshToken: refreshToken || '', // normalement c'est non vide car on a fait un checkTokens
    dispatch,state
  });
  logger.trace(...formatTrace('ApiServices - getCustomers - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:I_CUSTOMERS = await response.json();
  logger.debug('ApiServices - getCustomers - ok, returned body',body);
  return body;
};

/**
 * Crée un organisme audité
 * @param param0
 * @returns
 */
const create = async (args:I_CREATE_CUSTOMER_PARAMS):Promise<string> => {
  logger.debug('ApiServices - createCustomer - ...');
  const {
    accessToken,refreshToken,
    dispatch,state,
    type,name,id_customer,referentials,
  } = args;
  // pre-verification qu'on a bien des token, sinon sert a rien de faire la requete
  await _checkTokens(accessToken,refreshToken);
  // pre-verification qu'on a bien des valeurs obligatoires, sinon sert a rien de faire la requete
  if (isMissingField(args)) {
    const error = {
      error: true,
      errorCode: 1000,
      errorText: 'Missing field(s)',
      errorMessage: 'Missing field(s) to post new customer',
    };
    return Promise.reject(error);
  }
  // requete le endpoint
  const response = await _tryOrRefresh({
    request: (usedAccessToken:string) => {
      return postCustomerResponse({
        accessToken:usedAccessToken,
        type,name,id_customer,referentials,
      });
    },
    accessToken,
    refreshToken,
    dispatch,state
  });
  logger.trace(...formatTrace('ApiServices - createCustomer - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:string = await response.text();
  logger.debug('ApiServices - createCustomer - ok, returned body',body);
  logger.info(...formatInfo('Customer created!'));
  return body;
};

/**
 * Supprime un organisme audité
 * @param param0
 * @returns
 */
const remove = async ({accessToken,refreshToken,id,dispatch,state}:I_COMMON_DELETE_PARAMS):Promise<string> => {
  logger.debug('ApiServices - deleteCustomer - ...');
  // pre-verification qu'on a bien des token, sinon sert a rien de faire la requete
  await _checkTokens(accessToken,refreshToken);
  // requete le endpoint
  const response = await _tryOrRefresh({
    request: (usedAccessToken:string) => {
      return deleteCustomerResponse({accessToken:usedAccessToken,id});
    },
    accessToken: accessToken || '', // normalement c'est non vide car on a fait un checkTokens
    refreshToken: refreshToken || '', // normalement c'est non vide car on a fait un checkTokens
    dispatch,state
  });
  logger.trace(...formatTrace('ApiServices - deleteCustomer - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:string = await response.text();
  logger.debug('ApiServices - deleteCustomer - ok, returned body',body);
  logger.info(...formatInfo('Customer deleted!'));
  return body;
};


/**
 * Modifie un organisme audité
 * @param param0
 * @returns
 */
const update = async (args:I_PUT_CUSTOMER_PARAMS):Promise<string> => {
  logger.debug('ApiServices - updateCustomer - ...');
  const {
    accessToken,refreshToken,
    dispatch,state,
    id,
    type,name,id_customer,referentials,
  } = args;
  // pre-verification qu'on a bien des token, sinon sert a rien de faire la requete
  await _checkTokens(accessToken,refreshToken);
  // pre-verification qu'on a bien des valeurs obligatoires, sinon sert a rien de faire la requete
  if (isMissingField(args)) {
    const error = {
      error: true,
      errorCode: 1000,
      errorText: 'Missing field(s)',
      errorMessage: 'Missing field(s) to post new customer',
    };
    return Promise.reject(error);
  }
  // requete le endpoint
  const response = await _tryOrRefresh({
    request: (usedAccessToken:string) => {
      return putCustomerResponse({
        accessToken:usedAccessToken,id,
        type,name,id_customer,referentials,
      });
    },
    accessToken: accessToken || '', // normalement c'est non vide car on a fait un checkTokens
    refreshToken: refreshToken || '', // normalement c'est non vide car on a fait un checkTokens
    dispatch,state
  });
  logger.trace(...formatTrace('ApiServices - updateCustomer - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:string = await response.text();
  logger.debug('ApiServices - updateCustomer - ok, returned body',body);
  logger.info(...formatInfo('Customer updated!'));
  return body;
};

/**
 * Récupère l'organisme audité
 * @param param0
 * @returns
 */
const getItem = async ({accessToken,refreshToken,id,dispatch,state}:I_COMMON_GET_ITEM_PARAMS) => {
  logger.debug('ApiServices - getCustomer - ...');
  // pre-verification qu'on a bien des token, sinon sert a rien de faire la requete
  await _checkTokens(accessToken,refreshToken);
  // requete le endpoint
  const response = await _tryOrRefresh({
    request: (usedAccessToken:string) => {
      return getCustomerResponse({accessToken:usedAccessToken,id});
    },
    accessToken: accessToken || '', // normalement c'est non vide car on a fait un checkTokens
    refreshToken: refreshToken || '', // normalement c'est non vide car on a fait un checkTokens
    dispatch,state
  });
  logger.trace(...formatTrace('ApiServices - getCustomer - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:I_CUSTOMER = await response.json();
  logger.debug('ApiServices - getCustomer - ok, returned body',body);
  return body;
};


/**
 * Importe des organismes audités
 * @param param0
 * @returns
 */
const importList = async ({
  accessToken,refreshToken,file, dispatch,state
}:I_COMMON_IMPORT_PARAMS):Promise<I_IMPORT_CUSTOMERS_OK> => {
  logger.debug('ApiServices - importCustomers - ...');
  // pre-verification qu'on a bien des token, sinon sert a rien de faire la requete
  await _checkTokens(accessToken,refreshToken);
  // pre-verification qu'on a bien des valeurs obligatoires, sinon sert a rien de faire la requete
  if (!file) {
    const error = {
      error: true,
      errorCode: 1000,
      errorText: 'Missing file',
      errorMessage: 'Missing file to post audited customers',
    };
    return Promise.reject(error);
  }
  // requete le endpoint
  const response = await _tryOrRefresh({
    request: (usedAccessToken:string) => {
      return importCustomersResponse({
        accessToken:usedAccessToken,file,
      });
    },
    accessToken: accessToken || '', // normalement c'est non vide car on a fait un checkTokens
    refreshToken: refreshToken || '', // normalement c'est non vide car on a fait un checkTokens
    dispatch,state
  });
  logger.trace(...formatTrace('ApiServices - importCustomers - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:I_IMPORT_CUSTOMERS_OK = await response.json();
  logger.debug('ApiServices - importCustomers - ok, returned body',body);
  logger.info(...formatInfo('Customers imported!'));
  return body;
};

const CustomersServices = {
  getList,
  create,
  remove,
  update,
  getItem,
  importList,
};

export default CustomersServices;
